// ─────────────────────────────────────────────────────────────────────────────
// Crew — magic-link auth (Agent B).
//
// Flow: enter email → POST /auth/magic-link. In DEV the server returns the raw
// `devToken`, so we auto-verify it for a frictionless demo. Production (Agent D)
// stops returning devToken; the user clicks the link in their inbox and lands on a
// verify URL. As a belt-and-braces fallback we also expose a manual paste box so a
// token can always be exchanged by hand.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/Button';
import { Card, CardTitle } from '../components/Card';
import { TextField } from '../components/Field';
import { ErrorBanner } from '../components/Feedback';
import { errorMessage } from '../lib/format';

interface AuthScreenProps {
  onAuthed: () => void;
}

export function AuthScreen({ onAuthed }: AuthScreenProps) {
  const [email, setEmail] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Once a link is "sent" we show the paste fallback. devToken (DEV only) is
  // pre-filled so the manual path is one click in development too.
  const [linkSent, setLinkSent] = useState(false);
  const [manualToken, setManualToken] = useState('');

  async function requestLink(e: FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setRequesting(true);
    setError(null);
    try {
      const res = await api.requestMagicLink(email.trim());
      // DEV convenience: the server handed us the token — verify straight through.
      if (res.devToken) {
        await verifyToken(res.devToken);
        return;
      }
      setLinkSent(true);
    } catch (err) {
      setError(errorMessage(err, 'Could not send the magic link. Check the address and try again.'));
    } finally {
      setRequesting(false);
    }
  }

  async function verifyToken(token: string) {
    setVerifying(true);
    setError(null);
    try {
      await api.verify(token.trim());
      onAuthed();
    } catch (err) {
      setError(errorMessage(err, 'That link looks invalid or expired. Request a new one.'));
    } finally {
      setVerifying(false);
    }
  }

  return (
    <div className="mx-auto flex max-w-md flex-col gap-6 py-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-ink-900">Welcome to Crew</h1>
        <p className="mt-1 text-ink-700">
          Plan group experiences together — from a half-formed idea to a booked night out. Sign in
          with a magic link; no password required.
        </p>
      </div>

      {error && <ErrorBanner message={error} />}

      <Card>
        <CardTitle>Sign in</CardTitle>
        <form onSubmit={requestLink} className="mt-4 flex flex-col gap-4">
          <TextField
            label="Email address"
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            hint="We'll email you a one-tap link to sign in."
          />
          <Button type="submit" busy={requesting || verifying}>
            {linkSent ? 'Resend magic link' : 'Send magic link'}
          </Button>
        </form>
      </Card>

      {linkSent && (
        <Card>
          <CardTitle>Check your inbox</CardTitle>
          <p className="mt-1 text-sm text-ink-700">
            We sent a sign-in link to <span className="font-medium">{email}</span>. Tap it to
            continue. If you have the token, you can paste it below instead.
          </p>
          <div className="mt-4 flex flex-col gap-3">
            <TextField
              label="Sign-in token"
              value={manualToken}
              onChange={(e) => setManualToken(e.target.value)}
              placeholder="Paste the token from your email"
            />
            <Button
              variant="secondary"
              busy={verifying}
              disabled={!manualToken.trim()}
              onClick={() => verifyToken(manualToken)}
            >
              Verify token
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
